import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Konva from 'konva';

import { Modal, FlexBox } from '@eltoro-ui/components';

// models
import { SceneManager } from 'Components/CanvasEditor/models/SceneManager';

import ToolbarHeader from 'Components/CanvasEditor/components/ToolbarHeader';
import CanvasEditorFooter from 'Components/CanvasEditor/components/CanvasEditorFooter';
import SidebarPanel from 'Components/CanvasEditor/components/SidebarPanel';
import Editor from 'Components/CanvasEditor/components/Editor';

import cross from 'assets/Images/cross.png';

import { parseElements } from 'Components/CanvasEditor/utils';

import type {
  CanvasBackground,
  CanvasElement,
  ICanvasDimensions,
  ICanvasHistory,
} from 'Components/CanvasEditor/types';
import type { GenBannerCanvas } from 'types';

import './CanvasEditor.scss';

interface CanvasEditorProps {
  onCancel: VoidFunction;
  onApproveCanvas: (file: File) => void;
  canvas_banner: GenBannerCanvas;
}

export default function CanvasEditor({
  canvas_banner,
  onCancel,
  onApproveCanvas,
}: CanvasEditorProps) {
  const {
    canvas: { width: canvasWidth, height: canvasHeight },
    objects,
  } = canvas_banner;

  const location = useLocation();
  const width = new URLSearchParams(location.search).get('width');
  const height = new URLSearchParams(location.search).get('height');

  const stageRef = useRef<Konva.Stage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const transformerRef = useRef<Konva.Transformer>(null);

  const [sceneManager, setSceneManager] = useState<SceneManager | null>(null);

  const [activeTab, setActiveTab] = React.useState<string>('text');

  const [historyStep, setHistoryStep] = useState(0);
  const [history, setHistory] = useState<ICanvasHistory[]>([
    {
      elements: [],
      background: {
        type: 'color',
        value: '#ffffff',
      },
    },
  ]);

  const [activeCropper, setActiveCropper] = useState(false);
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [background, setBackground] = useState<CanvasBackground>({
    type: 'color',
    value: '#ffffff',
  });

  const [canvasDimensions, setCanvasDimensions] = useState<ICanvasDimensions>({
    canvasWidth: canvasWidth ?? 800,
    canvasHeight: canvasHeight ?? 600,
  });

  const saveBackgroundToHistory = useCallback(
    (newBackground: CanvasBackground) => {
      const newState: ICanvasHistory = { background: newBackground, elements };
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newState);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    },
    [history, historyStep, elements]
  );

  const saveElementsToHistory = useCallback(
    (newElements: CanvasElement[]) => {
      const newState: ICanvasHistory = { elements: newElements, background };
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push(newState);
      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
    },
    [history, historyStep, background]
  );

  // Undo functionality
  const undo = useCallback(() => {
    if (historyStep > 0) {
      const prevStep = historyStep - 1;
      setHistoryStep(prevStep);
      setElements(history[prevStep].elements);
      setSelectedId(null);
    }
  }, [history, historyStep]);

  // Redo functionality
  const redo = useCallback(() => {
    if (historyStep < history.length - 1) {
      const nextStep = historyStep + 1;
      setHistoryStep(nextStep);
      setElements(history[nextStep].elements);
      setSelectedId(null);
    }
  }, [history, historyStep]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const setDefaultElements = async () => {
    const elements = await parseElements(objects);
    setElements(elements as CanvasElement[]);
    setHistory([
      {
        elements: elements as CanvasElement[],
        background: {
          type: 'color',
          value: '#ffffff',
        },
      },
    ]);
  };

  useEffect(() => {
    setDefaultElements();
  }, [objects]);

  useEffect(() => {
    if (stageRef.current && layerRef.current) {
      const layer = layerRef.current;

      const manager = layer.findOne('#scene-manager');
      manager?.moveToTop();
    }
  }, [elements.length]);

  const onApprove = async () => {
    const stage = stageRef.current;
    if (!stage) return;

    const transformer = transformerRef.current;
    const scaleTransformer: Konva.Transformer = stage.findOne('#scale-transformer');

    transformer?.nodes([]);
    transformer?.getLayer()?.batchDraw();
    scaleTransformer?.nodes([]);
    scaleTransformer?.getLayer()?.batchDraw();

    await stage.toBlob({
      mimeType: 'image/jpeg',
      quality: 1,
      callback: blob => {
        const file = new File([blob], `${canvasWidth}x${canvasHeight}.png`, { type: 'image/png' });
        onApproveCanvas(file);
      },
    });
  };

  const onChangeCanvasDimensions = (dimensions: ICanvasDimensions) =>
    setCanvasDimensions(dimensions);

  const onSelectElement = (id: string | null) => setSelectedId(id);

  const onBackgroundChange = (background: CanvasBackground) => {
    saveBackgroundToHistory(background);
    setBackground(background);
  };

  const onElementChange = (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => {
    setElements(elements => {
      const newElements = elements.map(element => {
        if (element.id === id) return { ...element, ...props } as CanvasElement;
        return element;
      });

      if (saveToHistory) saveElementsToHistory(newElements);
      return newElements;
    });
  };

  const onAddElement = (element: CanvasElement) => {
    onSelectElement(element.id);
    setElements(elements => {
      const newElements = [...elements, element];
      saveElementsToHistory(newElements);

      return newElements;
    });
  };

  const onDeleteElement = (id: string) => {
    onSelectElement(null);

    const stage = stageRef.current;
    const scaleTransformer: Konva.Transformer | undefined = stage?.findOne('#scale-transformer');
    scaleTransformer?.nodes([]);
    scaleTransformer?.getLayer()?.batchDraw();

    setElements(elements => {
      const newElements = elements.filter(element => element.id !== id);
      saveElementsToHistory(newElements);

      return newElements;
    });
  };

  useEffect(() => {
    if (width && height)
      onChangeCanvasDimensions({
        canvasWidth: parseInt(width, 10),
        canvasHeight: parseInt(height, 10),
      });
  }, [location]);

  const selectedElement = elements.find(el => el.id === selectedId) ?? null;

  return (
    <Modal className="canvas-editor-modal">
      <img
        src={cross}
        onClick={onCancel}
        alt="cross"
        className="PersonalInfoForm__cross"
        role="presentation"
      />
      <FlexBox flexDirection="column" UNSAFE_className="canvas-container">
        <ToolbarHeader
          selectedElement={selectedElement}
          onElementChange={onElementChange}
          onDeleteElement={onDeleteElement}
          sceneManager={sceneManager}
          activeCropper={activeCropper}
          setActiveCropper={setActiveCropper}
          undo={undo}
          redo={redo}
          historyStep={historyStep}
          history={history}
        />
        <FlexBox UNSAFE_className="canvas-content">
          <SidebarPanel
            stageRef={stageRef}
            layerRef={layerRef}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onSelectElement={onSelectElement}
            onAddElement={onAddElement}
            onElementChange={onElementChange}
            onBackgroundChange={onBackgroundChange}
            background={background}
            selectedId={selectedId}
            elements={elements}
            setNewElements={setElements}
            onDeleteElement={onDeleteElement}
          />
          <Editor
            stageRef={stageRef}
            layerRef={layerRef}
            transformerRef={transformerRef}
            setActiveCropper={setActiveCropper}
            sceneManager={[sceneManager, setSceneManager]}
            canvasDimensions={canvasDimensions}
            elements={elements}
            onSelectElement={onSelectElement}
            selectedElement={selectedElement}
            selectedId={selectedId}
            onElementChange={onElementChange}
            background={background}
          />
        </FlexBox>
        <CanvasEditorFooter
          onCancel={onCancel}
          onApprove={onApprove}
          activeCropper={activeCropper}
        />
      </FlexBox>
    </Modal>
  );
}
